import { DocumentGroundingNoticeComponent } from '../document-grounding-notice/document-grounding-notice';
import { getControlStatusMeaning } from '../../utils/control-status.utils';
import { getEffectiveDocumentStatusMeaning } from '../../utils/document.utils';
import {
  Component,
  ChangeDetectionStrategy,
  DestroyRef,
  inject,
  input,
  output,
  signal,
  computed,
  model,
  effect,
  OnDestroy,
} from '@angular/core';
import { map } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlRecord, DocumentRecord, DocumentStatusEnum } from '../../models';
import { ContextDocument } from '../../models/httpApi.model';
import { LanguageBadgeComponent } from '../language-badge/language-badge';
import { OrgManagementApi } from '../../services/org-management-api/org-management-api';
import { StateStoreService } from '../../services/state-store/state-store';
import { FeatureId } from '../../constants/features.constants';
import { EntityLinkService } from '../../services/entity-link/entity-link';
import { AccessBannerComponent } from '../access-banner/access-banner';
import { DetailPanelComponent } from '../detail-panel/detail-panel';
import { EditablePanelTitleComponent } from '../editable-panel-title/editable-panel-title';
import { PanelTaskListComponent } from '../panel-task-list/panel-task-list';
import { PanelTabsComponent, PanelTab } from '../panel-tabs/panel-tabs';
import { EmptyStateComponent } from '../empty-state/empty-state';
import { AuditTimelineComponent } from '../audit-timeline/audit-timeline';
import {
  formatFileSize,
  getContextDocFileTypeLabel,
  getDocumentSubtypeLabel,
  getDocKindFromSubtypeCode,
  getEffectiveStatusLabel,
  getEffectiveStatusVariant,
  getContextDocIconConfig,
} from '../../utils/document.utils';
import { ContextDocPreviewComponent } from '../context-doc-preview/context-doc-preview';
import { createContextDocPreviewHandler } from '../../shared/context-doc-preview-handler';
import { TypeIconComponent } from '../type-icon/type-icon';
import { KindIconComponent } from '../kind-icon/kind-icon';
import { SectionIconComponent } from '../section-icon/section-icon';
import { StatusIndicatorComponent } from '../status-indicator/status-indicator';
import { TrustCenterVisibilityToggleComponent } from '../trust-center-visibility-toggle/trust-center-visibility-toggle';
import { denialOrFallback } from '../../utils/access-error.utils';
import { getUserFriendlyErrorMessage } from '../../utils/error.utils';
import { formatDate } from '../../utils/date.utils';
import { getInitials, getAvatarColor, getDisplayAuthor } from '../../utils/display.utils';
import {
  getControlStatusLabelShort,
  getControlStatusVariant as sharedControlStatusVariant,
} from '../../utils/control-status.utils';
import { formatControlSubtitle } from '../../utils/control.utils';
import { createPickerHandler } from '../../shared/picker-handler';
import { createAuditTrailLoader } from '../../shared/audit-trail-loader';
import { SkeletonDirective } from '../../shared/directives';

type TabId = 'overview' | 'controls' | 'history' | 'tasks';

@Component({
  selector: 'app-document-detail-panel',
  templateUrl: './document-detail-panel.html',
  styleUrl: './document-detail-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    DocumentGroundingNoticeComponent,
    MatIconModule,
    MatProgressSpinnerModule,
    TranslocoModule,
    DetailPanelComponent,
    AccessBannerComponent,
    EditablePanelTitleComponent,
    PanelTabsComponent,
    EmptyStateComponent,
    AuditTimelineComponent,
    TypeIconComponent,
    KindIconComponent,
    SectionIconComponent,
    StatusIndicatorComponent,
    LanguageBadgeComponent,
    TrustCenterVisibilityToggleComponent,
    PanelTaskListComponent,
    ContextDocPreviewComponent,
    SkeletonDirective,
  ],
})
export class DocumentDetailPanel implements OnDestroy {
  readonly getControlStatusMeaning = getControlStatusMeaning;

  readonly getEffectiveDocumentStatusMeaning = getEffectiveDocumentStatusMeaning;

  private readonly api = inject(OrgManagementApi);
  private readonly stateStore = inject(StateStoreService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly entityLink = inject(EntityLinkService);
  private readonly translationVersion = signal(0);

  /** VER-326: write-authority signal for this surface — mode-aware.
   *  MANAGER / SUPER_MANAGER / SELF_MANAGED short-circuit via role;
   *  STANDARD reads the `documents` feature's access-mode override.
   *  Name kept as `canManageCompliance` so the template's dozen-plus
   *  bindings stay untouched — semantic is unchanged, resolver only. */
  public readonly canManageCompliance = computed(() =>
    this.stateStore.canWrite(FeatureId.DOCUMENTS)
  );

  /** Hover tooltip surfaced on the disabled link / unlink buttons.
   *  Returns `null` when the caller is allowed to link — Angular omits
   *  the attribute entirely so the title doesn't shadow other tooltips
   *  on the enabled control. Reactive to language switches via
   *  `translationVersion`. */
  public readonly linkDisabledTooltip = computed<string | null>(() => {
    this.translationVersion();
    return this.canManageCompliance()
      ? null
      : this.transloco.translate('disabled_tooltips.only_managers_link_controls');
  });

  readonly document = input.required<DocumentRecord>();

  /** Trust Centre visibility eligibility.
   *
   *  Publishable = ``status ∈ {APPROVED, ACTIVE}`` AND not archived.
   *  Mirrors the BE ``_TRUST_CENTER_PUBLISHABLE_STATUSES`` filter in
   *  ``get_trust_center_documents``. ``APPROVED`` covers reviewer-
   *  signed-off docs; ``ACTIVE`` covers the current live version
   *  (post-activation). Draft / Awaiting-approval / Rejected /
   *  Inactive (superseded) all collapse the toggle. */
  readonly isTrustCenterEligible = computed(() => {
    const d = this.document();
    if (d.archived_at) return false;
    return d.status === DocumentStatusEnum.APPROVED || d.status === DocumentStatusEnum.ACTIVE;
  });
  readonly contextDocs = input<ContextDocument[]>([]);
  /** True while the parent route is exporting this document — drives
   *  the drawer's download button into a disabled + spinner state so
   *  the in-flight slot is visible (mirrors the card-menu spinner). */
  readonly isExporting = input<boolean>(false);
  /** Content is still being written: renaming would race the generation callback. */
  readonly isGenerating = input<boolean>(false);
  readonly closed = output<void>();
  readonly openDocument = output<DocumentRecord>();
  readonly downloadDocument = output<DocumentRecord>();
  readonly deleteDocument = output<DocumentRecord>();
  /** Emits the optimistically-updated record after a successful
   *  in-place rename so the parent route can refresh its cached
   *  document list / table cell without a full reload. */
  readonly documentUpdated = output<DocumentRecord>();

  readonly activeTab = signal<TabId>('overview');
  readonly linkedControls = signal<ControlRecord[]>([]);

  // All controls for this org (used to compute available controls for linking)
  private readonly allControls = signal<ControlRecord[]>([]);
  /** Why the picker has no list — distinct from having an empty one. See the
   *  error branch of `loadControls`. */
  public readonly controlsLoadError = signal<string | null>(null);
  public readonly controlsLoadErrorIsDenial = signal(false);

  private readonly _taskCount = signal(0);

  readonly panelTabs = computed<PanelTab[]>(() => {
    this.translationVersion();
    return [
      { id: 'overview', label: this.transloco.translate('panels_ext.tab_overview') },
      {
        id: 'controls',
        label: this.transloco.translate('panels_ext.tab_controls'),
        count: this.linkedControls().length || undefined,
      },
      { id: 'history', label: this.transloco.translate('panels_ext.tab_history') },
      {
        id: 'tasks',
        label: this.transloco.translate('panels_ext.tab_tasks'),
        count: this._taskCount() || undefined,
      },
    ];
  });
  readonly loadingControls = signal(false);
  readonly width = model(480);

  // Control linking via picker handler
  readonly controlPicker = createPickerHandler();

  readonly availableControls = computed(() => {
    const linkedIds = new Set(this.linkedControls().map(c => c.control_id));
    return this.allControls().filter(c => !linkedIds.has(c.control_id));
  });

  readonly filteredAvailableControls = computed(() => {
    const search = this.controlPicker.search().toLowerCase().trim();
    const controls = this.availableControls();
    if (!search) return controls;
    return controls.filter(
      c => c.name.toLowerCase().includes(search) || c.domain?.toLowerCase().includes(search)
    );
  });

  private readonly _resolvedContextDocs = signal<ContextDocument[]>([]);
  /** True while the constructor effect is resolving missing context-doc
   *  records via `getContextDocuments`. Templates use this to swap the
   *  real row markup for shimmer skeleton rows so the section doesn't
   *  silently pop in once the fetch lands. */
  private readonly _isLoadingContextDocs = signal(false);
  readonly isLoadingContextDocs = this._isLoadingContextDocs.asReadonly();
  private readonly _contextDocsError = signal<string | null>(null);
  readonly contextDocsError = this._contextDocsError.asReadonly();
  private readonly _contextDocsRetry = signal(0);
  private _contextDocsOwnerKey: string | null = null;

  readonly usedContextDocs = computed(() => {
    const ids = new Set(this.document().context_document_ids ?? []);
    if (!ids.size) return [];
    // Merge parent-provided and internally fetched, then filter to required IDs.
    // Parent list may be a partial page; resolved list fills in any missing ones.
    const merged = new Map<string, ContextDocument>();
    for (const d of this.contextDocs()) merged.set(d.context_document_id, d);
    for (const d of this._resolvedContextDocs()) merged.set(d.context_document_id, d);
    return [...merged.values()].filter(d => ids.has(d.context_document_id));
  });

  /** Single shared preview-modal handler — same factory the in-page
   *  drawer and `assessment-info-panel` consume. The refetch path
   *  uses the same `document_ids`-filtered list call the constructor
   *  effect uses, so a retry-mint also refreshes the resolved cache. */
  readonly contextDocPreview = createContextDocPreviewHandler({
    destroyRef: this.destroyRef,
    refetch: previewDoc => {
      const doc = this.document();
      const ids = previewDoc ? [previewDoc.context_document_id] : (doc.context_document_ids ?? []);
      if (!doc.org_id || !ids.length) return null;
      const ownerQuery =
        previewDoc?.owner_id &&
        (previewDoc.usage_scope === 'assessment' ||
          previewDoc.usage_scope === 'document' ||
          previewDoc.usage_scope === 'import' ||
          previewDoc.usage_scope === 'session' ||
          previewDoc.usage_scope === 'country_research')
          ? {
              source_owner_kind: previewDoc.usage_scope,
              source_owner_id: previewDoc.owner_id,
            }
          : {};
      return this.api
        .getContextDocuments(doc.org_id, {
          document_ids: ids,
          ...ownerQuery,
        })
        .pipe(
          map(resp => {
            this._resolvedContextDocs.set(resp.context_documents);
            return resp.context_documents;
          })
        );
    },
  });

  readonly getContextDocFileTypeLabel = getContextDocFileTypeLabel;
  readonly formatFileSize = formatFileSize;

  readonly canExport = computed(() => !!this.document().content_item?.s3_key);

  readonly author = computed(
    () => this.document().author_name ?? getDisplayAuthor(this.document().author)
  );

  // Map of control_id → name for resolving linked entity names in the audit timeline.
  readonly linkedEntityNameMap = computed<ReadonlyMap<string, string>>(() => {
    const map = new Map<string, string>();
    for (const ctrl of this.linkedControls()) {
      map.set(ctrl.control_id, ctrl.name);
    }
    return map;
  });

  // Audit trail
  readonly auditLoader = createAuditTrailLoader({
    getOrgId: () => this.document().org_id,
    entityType: 'document',
    getEntityId: () => this.document().document_id,
    api: (orgId, type, id) => this.api.getAuditTrail(orgId, type, id),
  });

  constructor() {
    this.transloco.langChanges$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.translationVersion.update(v => v + 1));

    // Fetch context docs directly when the parent input doesn't cover all required IDs
    effect((onCleanup: (fn: () => void) => void) => {
      const doc = this.document();
      this._contextDocsRetry();
      const ownerKey = `${doc.org_id}:${doc.document_id}`;
      if (this._contextDocsOwnerKey !== ownerKey) {
        this._resolvedContextDocs.set([]);
        this._contextDocsOwnerKey = ownerKey;
      }
      const ids = doc.context_document_ids;
      if (!ids?.length) {
        this._resolvedContextDocs.set([]);
        this._isLoadingContextDocs.set(false);
        this._contextDocsError.set(null);
        return;
      }
      const providedIds = new Set(
        this.contextDocs().map((d: ContextDocument) => d.context_document_id)
      );
      if (ids.every(id => providedIds.has(id))) {
        this._isLoadingContextDocs.set(false);
        this._contextDocsError.set(null);
        return; // parent covers all required IDs
      }
      this._isLoadingContextDocs.set(true);
      this._contextDocsError.set(null);
      const sub = this.api
        .getContextDocuments(doc.org_id, {
          document_ids: ids,
          source_owner_kind: 'document',
          source_owner_id: doc.document_id,
        })
        .subscribe({
          next: (resp: { context_documents: ContextDocument[] }) => {
            this._resolvedContextDocs.set(resp.context_documents);
            this._isLoadingContextDocs.set(false);
          },
          error: (err: unknown) => {
            this._contextDocsError.set(
              getUserFriendlyErrorMessage(
                err,
                this.transloco.translate<string>('context_documents.load_failed'),
                this.transloco
              )
            );
            this._isLoadingContextDocs.set(false);
          },
        });
      onCleanup(() => {
        sub.unsubscribe();
        this._isLoadingContextDocs.set(false);
      });
    });

    effect(() => {
      this.document();
      this.activeTab.set('overview');
      this.controlPicker.show.set(false);
      this.controlPicker.search.set('');
      this.loadData();
      this.auditLoader.load();
    });
  }

  retryContextDocs(): void {
    this._contextDocsRetry.update(value => value + 1);
  }

  ngOnDestroy(): void {
    this.auditLoader.destroy();
  }

  // Shared utils exposed to template
  readonly getDocumentSubtypeLabel = getDocumentSubtypeLabel;
  readonly getDocKindFromSubtypeCode = getDocKindFromSubtypeCode;
  readonly getContextDocIconConfig = getContextDocIconConfig;
  readonly getEffectiveStatusLabel = getEffectiveStatusLabel;
  readonly getEffectiveStatusVariant = getEffectiveStatusVariant;
  readonly formatDate = formatDate;
  readonly getInitials = getInitials;
  readonly getAvatarColor = getAvatarColor;
  readonly formatControlSubtitle = formatControlSubtitle;

  getControlStatusVariant = sharedControlStatusVariant;
  getControlStatusLabel = getControlStatusLabelShort;

  setTab(tab: TabId): void {
    this.activeTab.set(tab);
  }

  onClose(): void {
    this.closed.emit();
  }

  /** Navigate to a linked control's detail surface (controls list with the
   *  control panel popped). Closes this panel so the target's drawer isn't
   *  stacked behind it. Mirrors the task panel's `openLinkedEntity`. */
  openLinkedControl(controlId: string): void {
    this.entityLink.navigateTo('control', controlId, this.document().org_id);
    this.closed.emit();
  }

  onOpenDocument(): void {
    this.openDocument.emit(this.document());
  }

  onDownloadDocument(): void {
    if (this.isExporting()) return;
    this.downloadDocument.emit(this.document());
  }

  onDeleteDocument(): void {
    this.deleteDocument.emit(this.document());
  }

  /** Commit handler for `<app-editable-panel-title>`. Mirrors the
   *  task / control / framework drawers — PUT `/documents/:id` with
   *  the new name and emit the updated record so the parent route
   *  can refresh its list. No-op when the user isn't allowed to
   *  manage compliance (the title is rendered read-only in that
   *  case so this is a defence-in-depth check). */
  onTitleCommit(newName: string): void {
    if (!this.canManageCompliance()) return;
    const doc = this.document();
    const trimmed = newName.trim();
    if (!trimmed || trimmed === doc.name) return;
    this.api
      .updateDocument(doc.document_id, { name: trimmed })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.documentUpdated.emit({ ...doc, name: trimmed }),
        error: err => console.error('Failed to rename document:', err),
      });
  }

  confirmLinkControls(): void {
    const selectedIds = [...this.controlPicker.selectedIds()];
    if (selectedIds.length === 0) return;

    const controlsToLink = this.availableControls().filter(c =>
      this.controlPicker.selectedIds().has(c.control_id)
    );
    if (controlsToLink.length === 0) return;

    this.controlPicker.startLinking();
    let completed = 0;
    // Keep the error object, not just a boolean: when the caller lacks
    // `document_write` every request in the fan-out fails identically, and
    // only the object can tell the banner to say why instead of "try again".
    let firstError: unknown = null;
    const docId = this.document().document_id;

    const settle = () => {
      if (completed !== controlsToLink.length) return;
      if (!firstError) {
        this.controlPicker.finishLinking();
        return;
      }
      const { message, isDenial } = denialOrFallback(
        firstError,
        this.transloco.translate('panels_ext.control_failed_link_partial'),
        this.transloco
      );
      this.controlPicker.finishLinking(message, isDenial);
    };

    for (const control of controlsToLink) {
      const orgId = control.org_id;
      if (!orgId) {
        // Counts as done, and settles here too — a skipped *last* item
        // otherwise left the picker spinning with no request in flight.
        completed++;
        settle();
        continue;
      }
      this.api.linkControlDocument(orgId, control.control_id, docId).subscribe({
        next: () => {
          this.linkedControls.update(list => [...list, control]);
          completed++;
          settle();
        },
        error: err => {
          console.error('Failed to link control:', err);
          firstError ??= err;
          completed++;
          settle();
        },
      });
    }
  }

  unlinkControl(control: ControlRecord): void {
    const orgId = control.org_id;
    if (!orgId) return;
    this.controlPicker.startUnlinking(control.control_id);

    this.api
      .unlinkControlDocument(orgId, control.control_id, this.document().document_id)
      .subscribe({
        next: () => {
          // Optimistically remove from linked list
          this.linkedControls.update(list => list.filter(c => c.control_id !== control.control_id));
          this.controlPicker.finishUnlinking();
        },
        error: err => {
          console.error('Failed to unlink control:', err);
          this.controlPicker.finishUnlinking();
        },
      });
  }

  onTaskCountChanged(count: number): void {
    this._taskCount.set(count);
  }

  /** Load controls and control-document link records for the document's org. */
  private loadData(): void {
    const orgId = this.document().org_id;
    if (!orgId) return;
    this.loadingControls.set(true);
    this.api.getControls(orgId).subscribe({
      next: controls => {
        const docId = this.document().document_id;
        this.allControls.set(controls);
        const linked = controls.filter(
          c => c.documents?.some(d => d.document_id === docId) ?? false
        );
        this.linkedControls.set(linked);
        this.loadingControls.set(false);
      },
      error: err => {
        // Keep the reason. Swallowing it left `allControls` empty, which the
        // picker renders as "All controls linked" — a claim about the data
        // offered to someone who was simply refused the list.
        const { message, isDenial } = denialOrFallback(
          err,
          this.transloco.translate('panels_ext.controls_load_failed'),
          this.transloco
        );
        this.allControls.set([]);
        this.controlsLoadError.set(message);
        this.controlsLoadErrorIsDenial.set(isDenial);
        this.loadingControls.set(false);
      },
    });
  }
}
